import { createContext, useContext } from 'react';

export interface FormData {
  firstName: string;
  lastName: string;
  major: string;
  minor: string;
  /** Up to 4 clubs picked from the localized list. */
  clubs: string[];
}

export const EMPTY_FORM: FormData = {
  firstName: '',
  lastName: '',
  major: '',
  minor: '',
  clubs: [],
};

export function fullName(form: FormData): string {
  return `${form.firstName} ${form.lastName}`.trim();
}

export type ShareImages = { '3x4': string; '9x16': string };

/** A completed episode quiz. Stores the raw score plus the name used at the
 * time so each report card renders consistently even if the name changes. */
export interface QuizResult {
  /** Episode number (2–8). */
  episode: number;
  /** Number answered correctly. */
  correct: number;
  /** Total questions (always 10, stored for forward-safety). */
  total: number;
  firstName: string;
  lastName: string;
}

/** Completed quizzes keyed by episode number. */
export type QuizResults = Record<number, QuizResult>;

export interface FlowState {
  form: FormData;
  /** Cropped portrait as a data URL (19:23) ready to render on the lockup. */
  photoDataUrl: string | null;
  /** Composited isolated-card PNG (lockup over card.png) shown on /welcome. */
  cardImageDataUrl: string | null;
  /** Composited share PNGs for both sizes, ready to download from /share. */
  shareImages: ShareImages | null;
  setForm: (next: FormData) => void;
  setPhoto: (dataUrl: string | null) => void;
  setCardImage: (dataUrl: string | null) => void;
  setShareImages: (images: ShareImages | null) => void;
  /** True when the form is sufficiently filled to view /welcome and /share. */
  hasFormData: boolean;
  hasPhoto: boolean;
  /** Completed episode quizzes (persisted to localStorage). */
  quizResults: QuizResults;
  /** Record (or overwrite) a completed quiz for one episode. */
  setQuizResult: (result: QuizResult) => void;
  /** Set just the shared first/last name without touching major/minor/clubs. */
  setName: (firstName: string, lastName: string) => void;
  /** True once the user edits any ID-card field this visit — gates the
   * leave-page confirm so a pristine form doesn't prompt. */
  idFormTouched: boolean;
  setIdFormTouched: (touched: boolean) => void;
}

export const FlowContext = createContext<FlowState | null>(null);

export function useFlow(): FlowState {
  const ctx = useContext(FlowContext);
  if (!ctx) throw new Error('useFlow must be used inside <FlowProvider>');
  return ctx;
}
